// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, PropsWithChildren, useLayoutEffect, useMemo, useRef, useState } from "react";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";
import { lineEllipsisStyles } from "@store/Themes/common/mixins/typography";

export interface TruncatedTextByLinePresentationProps extends ExpandStyles<TruncatedTextByLineStyles> {}

export interface TruncatedTextByLineProps extends TruncatedTextByLinePresentationProps {
    alwaysShowChildren?: boolean;
    lineCount: number;
    text?: string;
}

export interface TruncatedTextByLineStyles {
    childrenWrapper?: InjectableCss;
    text?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const truncatedTextByLineStyles: TruncatedTextByLineStyles = {
    wrapper: {
        css: css`
            ${lineEllipsisStyles(1)}
            max-height: 1lh;

            &::before {
                content: "";
                float: right;
            }
        `,
    },
};

const TruncatedTextByLine: FC<PropsWithChildren<TruncatedTextByLineProps>> = ({
    alwaysShowChildren,
    children,
    extendedStyles,
    lineCount,
    overridedStyles,
    text,
}) => {
    const baseStyles = useExpandProps(truncatedTextByLineStyles, extendedStyles, overridedStyles);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const styles = useMemo(() => {
        const lineClamp = lineCount > 0 ? lineCount : 1;

        return mergeProps(baseStyles, {
            wrapper: {
                css: css`
                    --line-clamp: ${lineClamp};
                    -webkit-line-clamp: var(--line-clamp);
                    max-height: calc(var(--line-clamp) * 1lh);

                    &::before {
                        height: calc((var(--line-clamp) - 1) * 1lh);
                    }
                `,
            },
            childrenWrapper: {
                css: css`
                    ${() => {
                        if (isTruncated) {
                            return css`
                                clear: both;
                                float: right;
                            `;
                        }
                    }}
                `,
            },
        });
    }, [baseStyles, isTruncated, lineCount]);

    useLayoutEffect(() => {
        if (!wrapperRef.current) {
            return;
        }

        const observer = new ResizeObserver(entries => {
            if (!entries.length) {
                return;
            }

            const { scrollHeight, clientHeight } = entries[0].target;
            setIsTruncated(scrollHeight > clientHeight);
        });

        observer.observe(wrapperRef.current);

        return () => observer.disconnect();
    }, []);

    if (!text) {
        return null;
    }

    const parsedChildren = children ? (
        <StyledWrapper {...styles.childrenWrapper} as="span">
            {children}
        </StyledWrapper>
    ) : null;

    return (
        <StyledWrapper {...styles.wrapper} ref={wrapperRef}>
            {isTruncated && parsedChildren}
            <Typography {...styles.text}>{text}</Typography>
            {!isTruncated && alwaysShowChildren && parsedChildren}
        </StyledWrapper>
    );
};

export default TruncatedTextByLine;
